import React from 'react';
import { Link } from 'react-router-dom';
import type { Product } from './products';

interface ProductSwitcherProps {
  /** Products the tenant is entitled to, in display order. */
  products: Product[];
  /** Current route path, used to mark the active product. */
  pathname: string;
  loading?: boolean;
}

const ProductIcon: React.FC<{ product: Product }> = ({ product }) => {
  const Icon = product.icon;
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white"
      style={{ backgroundColor: product.accent }}
      aria-hidden="true"
    >
      <Icon size={15} strokeWidth={2} />
    </span>
  );
};

/**
 * Product_Switcher: the icon list of Features the tenant is entitled to.
 * With exactly one Feature it renders a static label instead of a switcher.
 * Requirements: 2.1, 2.2
 */
const ProductSwitcher: React.FC<ProductSwitcherProps> = ({ products, pathname, loading = false }) => {
  if (loading) {
    return (
      <div className="flex flex-col gap-1" data-testid="product-switcher-loading" aria-busy="true">
        {[0, 1].map((i) => (
          <div key={i} className="h-[44px] animate-pulse rounded-lg bg-white/[.06]" />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <p className="px-2.5 text-[13px] leading-snug text-[#b8afd0]">
        No products are enabled for your account. Ask your administrator to enable one.
      </p>
    );
  }

  if (products.length === 1) {
    const [only] = products;
    return (
      <div
        className="flex items-center gap-2.5 rounded-lg px-2.5 py-[9px] text-sm font-bold text-white"
        data-testid="product-label"
      >
        <ProductIcon product={only} />
        <span className="truncate">{only.name}</span>
      </div>
    );
  }

  return (
    <nav aria-label="Products" className="flex flex-col gap-0.5">
      {products.map((product) => {
        const active = pathname === product.href || pathname.startsWith(`${product.href}/`);
        return (
          <Link
            key={product.id}
            to={product.href}
            aria-current={active ? 'page' : undefined}
            className={`flex items-center gap-2.5 rounded-lg px-2.5 py-[9px] text-sm transition-colors ${
              active
                ? 'bg-white/[.08] font-bold text-white'
                : 'font-normal text-[#c8c0dd] hover:bg-white/[.05] hover:text-white'
            }`}
          >
            <ProductIcon product={product} />
            <span className="truncate">{product.name}</span>
          </Link>
        );
      })}
    </nav>
  );
};

export default ProductSwitcher;
